import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { FroalaOptions } from 'froala-editor';
import { TOKEN } from 'shared/util-common';
import 'froala-editor/js/languages/de.js';

import { AuthStorageService } from '@treetop/authentication';

import {
  ADD_ANCHOR_LINK_BUTTON_PREFIX,
  ADD_ANCHOR_LINK_CONTAINER_PREFIX,
  addAnchorLinkEditPopup,
  addAnchorLinkFroalaPopup,
  createFroalaAnchorLinkPlugin,
  registerAddAnchorLinkCommand,
} from './plugins/anchor-link.froala.plugin';
import { createCustomFroalaPopup } from './plugins/popup.froala.plugin';
import {
  ADD_REFERENCE_BUTTON_PREFIX,
  ADD_REFERENCE_CONTAINER_PREFIX,
  addReferenceFroalaPopup,
  createFroalaReferencesPlugin,
  registerAddReferenceCommand,
} from './plugins/references.froala.plugin';
import {
  colorFormattingRefreshOnShow,
  COMMAND_ID_PREFIXES,
  registerDropdown,
} from './rich-text-editor-dropdown.util';

export type FroalaToolbarType = 'normal' | 'basic' | 'reduced';

@Injectable({
  providedIn: 'root',
})
export class FroalaConfigService {
  private translocoService = inject(TranslocoService);
  private authStorageService = inject(AuthStorageService);

  constructor() {
    registerAddAnchorLinkCommand(); // putting the link span to the editor content
    registerAddReferenceCommand(); // putting the reference span to the editor content

    createFroalaAnchorLinkPlugin(); // all the link functionality
    createFroalaReferencesPlugin(); // all the reference functionality
    addAnchorLinkEditPopup();

    addAnchorLinkFroalaPopup(this.translocoService.translate('GuidelineEditor.InternalLinkPopover.AddInternalLinkButton')); // the button in the toolbar, that opens the popup for the link
    addReferenceFroalaPopup(this.translocoService.translate('ReferencePopover.AddReferenceButton')); // the button in the toolbar, that opens the popup for the reference

    createCustomFroalaPopup('ttAnchorLinkPlugin', ADD_ANCHOR_LINK_BUTTON_PREFIX, ADD_ANCHOR_LINK_CONTAINER_PREFIX); // custom popup for the link
    createCustomFroalaPopup('ttReferencePlugin', ADD_REFERENCE_BUTTON_PREFIX, ADD_REFERENCE_CONTAINER_PREFIX); // custom popup for the reference

    registerDropdown(
      COMMAND_ID_PREFIXES.TEXT_FORMATTING,
      this.translocoService.translate('RichTextEditor.TextFormatting'),
      'TEXT_FORMATTING',
      'underline',
      'underline',
    );

    registerDropdown(
      COMMAND_ID_PREFIXES.INDENT_FORMATTING,
      this.translocoService.translate('RichTextEditor.Indent'),
      'INDENT_FORMATTING',
      'indent',
      'indent',
    );

    registerDropdown(
      COMMAND_ID_PREFIXES.COLOR_FORMATTING,
      this.translocoService.translate('RichTextEditor.Color'),
      'COLOR_FORMATTING',
      'textColor',
      'textColor',
      colorFormattingRefreshOnShow,
    );

  }

  public getToolbar(
    toolbarType: FroalaToolbarType,
    options: {
      disabledTools?: string[];
    } = {},
  ): string[] {
    const toolbarPart1 = ['bold', 'italic', 'fontSize'];
    const toolbarPart2 = ['formatOL', 'formatUL'];
    const toolbarPart3 = ['insertImage', 'insertVideo', 'insertTable', 'specialCharacters', 'clearFormatting', 'undo', 'redo', 'insertLink'];

    const customTools = [ADD_ANCHOR_LINK_BUTTON_PREFIX, ADD_REFERENCE_BUTTON_PREFIX];

    let toolbar;

    if (toolbarType === 'normal') {
      toolbar = [
        ...toolbarPart1,
        COMMAND_ID_PREFIXES.TEXT_FORMATTING,
        COMMAND_ID_PREFIXES.COLOR_FORMATTING,
        ...toolbarPart2,
        'align',
        COMMAND_ID_PREFIXES.INDENT_FORMATTING,
        ...toolbarPart3,
        ...customTools,
      ];
    } else if (toolbarType === 'reduced') {
      toolbar = [];
    } else if (toolbarType === 'basic') {
      toolbar = ['bold', 'italic', 'textFormatting', 'underline', 'formatOL', 'formatUL'];
    } else {
      toolbar = [
        ...toolbarPart1,
        COMMAND_ID_PREFIXES.TEXT_FORMATTING,
        COMMAND_ID_PREFIXES.COLOR_FORMATTING,
        ...toolbarPart2,
        'align',
        COMMAND_ID_PREFIXES.INDENT_FORMATTING,
        ...toolbarPart3,
        ...customTools,
      ];
    }

    if (options.disabledTools && options.disabledTools.length > 0) {
      toolbar = toolbar.filter((tool) => !options.disabledTools?.includes(tool));
    }

    return toolbar;
  }

  public getEditorConfigFroala(uploadUrl: string, placeholderText: string, translocoService: TranslocoService): Partial<FroalaOptions> {
    const token = this.authStorageService.getItem(TOKEN);
    const language = translocoService.getActiveLang();

    return {
      key: 'xGE6oF4B3D3A11D6D7F6B2HxlrC-22VKOG1FGULVKHXDXNDXc2a1Kd1SNdF3H3A8C6D4A3D3E3C2A17==',
      placeholderText,
      language,
      charCounterCount: false,
      initOnClick: true,
      listAdvancedTypes: true,
      toolbarBottom: true,
      toolbarSticky: true,
      linkAlwaysBlank: true,

      colorsBackground: [
        '#000000',
        '#4D4D4D',
        '#999999',
        '#E6E6E6',
        '#FFFFFF',
        '#E64C4C',
        '#E6994C',
        '#E6E64C',
        '#99E64C',
        '#4CE64C',
        'REMOVE',
      ],
      linkList: [],
      linkStyles: {
        linkStyleColor: translocoService.translate('Blue'),
        linkStyleUnderline: translocoService.translate('Underline'),
        linkStyleRemoveUnderline: translocoService.translate('Without'),
      },
      pluginsEnabled: [
        'lists',
        'paragraphFormat',
        'paragraphStyle',
        'align',
        'colors',
        'image',
        'video',
        'link',
        'table',
        'wordPaste',
        'specialCharacters',
        'lineBreaker',
        'ttAnchorLinkPlugin',
        'ttReferencePlugin',
        'AnchorLink',
        'References',
        'anchorLinkEditPopup',
      ],
      tableEditButtons: [
        'tableRemove',
        '|',
        'tableRows',
        'tableColumns',
        'tableStyle',
        '-',
        'tableCells',
        'tableCellBackground',
        'tableCellVerticalAlign',
        'tableCellHorizontalAlign',
        'tableCellStyle',
      ],
      tableStyles: {
        treetopTableStyle: translocoService.translate('TextEditor.TreetopStandardTable'),
        treetopTableStyleWithHeader: translocoService.translate('TextEditor.TreetopStandardTableWithHeader'),
      },
      tableCellStyles: {
        paddingMin: translocoService.translate('TextEditor.PaddingMin'),
        paddingMed: translocoService.translate('TextEditor.PaddingMed'),
        paddingMax: translocoService.translate('TextEditor.PaddingMax'),
        cellHeader: translocoService.translate('TextEditor.Header'),
      },
      tableDefaultWidth: '100%',
      tableDefaultAlign: 'left',
      pasteDeniedAttrs: [],
      //Keep table style on copy paste
      pasteAllowedStyleProps: [
        'padding',
        'padding-left',
        'padding-right',
        'padding-top',
        'padding-bottom',
        'background-color',
        'background',
        'text-align',
        'border',
        'border-collapse',
        'border-width',
        'border-style',
        'border-color',
        'width',
        'margin',
        'margin-left',
        'margin-right',
        'margin-top',
        'margin-bottom',
        'font-weight',
        'font-style',
        'vertical-align',
      ],
      imageUploadRemoteUrls: true,
      imageUploadURL: uploadUrl,
      imageUploadMethod: 'POST',
      videoUploadURL: uploadUrl,
      videoUploadMethod: 'POST',
      videoUploadParam: 'file',
      // Froala validates MIME subtypes: MOV uses video/quicktime, OGV uses video/ogg.
      videoAllowedTypes: ['mp4', 'quicktime', 'webm', 'ogg'],
      // Leave room for multipart headers within the endpoint's 200 MB request limit.
      videoMaxSize: 190_000_000,
      videoInsertButtons: ['videoBack', '|', 'videoUpload'],
      requestHeaders: {
        Authorization: `Bearer ${token}`,
      },
      imageEditButtons: [
        'imageReplace',
        'imageSize',
        'imageCaption',
        '-',
        'imageRemove',
        'imageLink',
        'imageAlt',
        '-',
        'imageStyle',
        'imageAlign',
        'imageDisplay',
      ],
      linkEditButtons: ['linkOpen', 'linkEdit', 'linkRemove', 'anchorLinkEdit'],
      lineBreakerOffset: 40,
      events: {
        blur: function () {
          this.toolbar?.hide?.();
        },
        focus: function () {
          this.toolbar?.show?.();
        },
        'table.inserted': function (table: HTMLElement) {
          table.style.removeProperty('margin');
          // Tag new tables so _froala.scss can drop the float on full-width ones
          // without touching pre-existing content.
          table.classList.add('tt-managed-table');
        },
      },
    };
  }
}
